import { formatDateBR } from "./dates";
import { daysBetween } from "./finance";
import { formatBRL } from "./money";

/**
 * Cobrador (T3.7, 06-ia-agentes §3): régua de cobrança determinística. Cada conta a receber em aberto recebe, no
 * máximo, uma mensagem por passo da régua (dias em relação ao vencimento: negativo = antes); o passo devido é o maior
 * já alcançado (uma conta que entra atrasada há 40 dias recebe só o passo de 30, nunca os anteriores). O tom vem do
 * passo e do histórico do pagador. O modelo só redige o parágrafo pessoal; valor, vencimento e meios de pagamento
 * são montados pelo código.
 */

export const DEFAULT_COLLECTION_STEPS: readonly number[] = [-3, 0, 3, 10, 30];
export const MAX_COLLECTION_STEPS = 8;

export function validCollectionSteps(steps: readonly number[]): boolean {
  return (
    steps.length >= 1 &&
    steps.length <= MAX_COLLECTION_STEPS &&
    new Set(steps).size === steps.length &&
    steps.every((s) => Number.isInteger(s) && s >= -30 && s <= 90)
  );
}

/** Maior passo ≤ dias de atraso (null = nenhum passo alcançado ainda). */
export function dueCollectionStep(daysLate: number, steps: readonly number[]): number | null {
  let best: number | null = null;
  for (const s of steps) if (s <= daysLate && (best === null || s > best)) best = s;
  return best;
}

export type PayerHistory = { paidCount: number; avgDaysLate: number };

/** Contas pagas (já filtradas nos últimos 12 meses): quantidade e atraso médio (pago antes conta zero). */
export function payerHistory(paid: readonly { dueDate: string; paidDate: string }[]): PayerHistory {
  if (!paid.length) return { paidCount: 0, avgDaysLate: 0 };
  const late = paid.reduce((s, p) => s + Math.max(0, daysBetween(p.dueDate, p.paidDate)), 0);
  return { paidCount: paid.length, avgDaysLate: Math.round(late / paid.length) };
}

export type CollectionTone = "lembrete" | "vencimento" | "cordial" | "firme";

/** Tom: antes = lembrete; no dia = vencimento; até 9 dias = cordial (firme com mau histórico); 10+ = firme
 * (cordial para bom pagador até 29 dias). Histórico só conta com 3+ pagamentos. */
export function collectionTone(step: number, h: PayerHistory): CollectionTone {
  if (step < 0) return "lembrete";
  if (step === 0) return "vencimento";
  const known = h.paidCount >= 3;
  if (step < 10) return known && h.avgDaysLate > 15 ? "firme" : "cordial";
  return known && h.avgDaysLate <= 2 && step < 30 ? "cordial" : "firme";
}

export function collectionStepLabel(step: number): string {
  if (step < 0) return `${-step} dia${step === -1 ? "" : "s"} antes do vencimento`;
  if (step === 0) return "No vencimento";
  return `${step} dia${step === 1 ? "" : "s"} de atraso`;
}

/** Primeiro nome com inicial maiúscula (o modelo recebe só isto do cliente). */
export function firstName(name: string): string {
  // Revisão de segurança (T3.7): o primeiro nome vai ao modelo e ao e-mail; só letras (e hífen/apóstrofo entre
  // elas), até 30 — sem números, chaves Pix, sinais ou "ordens" coladas ao nome do cadastro.
  const w = (name.trim().split(/\s+/)[0] ?? "")
    .replace(/[^\p{L}'’-]/gu, "")
    .replace(/^['’-]+|['’-]+$/g, "")
    .replace(/(['’-])['’-]+/g, "$1")
    .slice(0, 30);
  if (!w) return "cliente";
  const lower = w.toLocaleLowerCase("pt-BR");
  return lower.charAt(0).toLocaleUpperCase("pt-BR") + lower.slice(1);
}

export function collectionSubject(
  tone: CollectionTone,
  companyName: string,
  amountCents: number,
  dueDate: string,
): string {
  const v = formatBRL(amountCents);
  const d = formatDateBR(dueDate);
  switch (tone) {
    case "lembrete":
      return `Lembrete: conta de ${v} vence em ${d} — ${companyName}`;
    case "vencimento":
      return `Sua conta de ${v} vence hoje (${d}) — ${companyName}`;
    case "cordial":
      return `Conta de ${v} vencida em ${d} — ${companyName}`;
    case "firme":
      return `Conta em atraso desde ${d} (${v}) — ${companyName}`;
  }
}

/** Parágrafo padrão (sem modelo). */
export function collectionFallbackText(tone: CollectionTone, name: string, daysLate: number): string {
  const n = Math.abs(daysLate);
  const dias = `${n} dia${n === 1 ? "" : "s"}`;
  switch (tone) {
    case "lembrete":
      return `Olá, ${name}! Passando para lembrar que a sua conta vence em ${dias}. Os dados para pagamento estão logo abaixo.`;
    case "vencimento":
      return `Olá, ${name}! A sua conta vence hoje. Os dados para pagamento estão logo abaixo.`;
    case "cordial":
      return `Olá, ${name}. Não identificamos o pagamento da sua conta, vencida há ${dias}. Se já pagou, por favor desconsidere; se não, os dados estão logo abaixo.`;
    case "firme":
      return `Olá, ${name}. A sua conta está em atraso há ${dias}. Pedimos que regularize o pagamento o quanto antes ou fale conosco para combinarmos uma solução.`;
  }
}
